import { useCallback } from "react";
import {
  createTimeoutPromise,
  formatSakTopics,
  getStateName,
  processSponsorMessage,
  requestEletter,
  RequestEletterParams,
  retrieveSakQuestionnaireData,
  storeSakChildCompletion,
  triggerEvent,
  triggerEventOnce,
  wvusLogger
} from "..";
import { ChildData } from "../../types";
import questionnaireConfig from "../../components/Questionnaire/questionnaireConfig.json";

interface PhotoData {
  mediaUrl: string;
  mediaHandshake: string;
}

interface EletterConfig {
  baseUrl: string;
  endpoint: string;
  apiKey: string;
}

interface UseEletterSubmissionProps {
  childData: ChildData | null;
  childId: string;
  campaignSourceCode: string;
  limitedTrustHandshake: string;
  limitedTrustMarker: string;
  supporterName: string;
  supporterNumber: string;
  supporterState: string;
  uploadedPhotoData: PhotoData | null;
  eletterConfig: EletterConfig;
  setLoading: (loading: boolean) => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

export function useEletterSubmission({
  childData,
  childId,
  campaignSourceCode,
  limitedTrustHandshake,
  limitedTrustMarker,
  supporterName,
  supporterNumber,
  supporterState,
  uploadedPhotoData,
  eletterConfig,
  setLoading,
  onSuccess,
  onError
}: UseEletterSubmissionProps) {
  const ELETTER_TIMEOUT_MS = 45000;

  const sendEletter = useCallback(async () => {
    setLoading(true);

    // Get the SAK questionnaire from local storage if available.
    const parsedQuestionnaireData = retrieveSakQuestionnaireData();

    // Get template and compose letter.
    const topic = parsedQuestionnaireData?.topic || "";
    const response = parsedQuestionnaireData?.response || "";
    const topics = formatSakTopics(parsedQuestionnaireData);

    try {
      let letterText: string;

      if (!topic || !response) {
        letterText = questionnaireConfig.genericLetterTemplate
          .replace(/\{sponsorName\}/g, supporterName || "")
          .replace(/\{childName\}/g, getStateName(childData?.givenName || ""));
      } else {
        const topicConfig = questionnaireConfig.topics.find((t) => t.id === topic);

        if (!topicConfig || !topicConfig.template) {
          throw new Error(`No template found for topic: ${topic}`);
        }

        letterText = topicConfig.template
          .replace(/\{sponsorName\}/g, supporterName || "")
          .replace(/\{sponsorState\}/g, getStateName(supporterState || ""))
          .replace(/\{response\}/g, processSponsorMessage(response))
          .replace(/\{childName\}/g, childData?.givenName || "");
      }

      const requestParams: RequestEletterParams = {
        campaignSourceCode,
        supportOfficeChildId: childData?.supportOfficeChildId || "",
        countryCode: childData?.countryCode || "",
        eletterConfig: {
          baseUrl: eletterConfig.baseUrl,
          endpoint: eletterConfig.endpoint,
          apiKey: eletterConfig.apiKey
        },
        letterText,
        limitedTrustHandshake,
        limitedTrustMarker,
        mediaHandshake: uploadedPhotoData?.mediaHandshake || "",
        mediaUrl: uploadedPhotoData?.mediaUrl || "",
        supporterNumber
      };

      const eletterPromise = requestEletter({ ...requestParams });
      await createTimeoutPromise(eletterPromise, ELETTER_TIMEOUT_MS);

      triggerEventOnce("sak_message_send_success", { topics });

      // Save completed payload to local storage to prevent duplicate entries
      storeSakChildCompletion(childId, requestParams);

      window.scrollTo(0, 0);
      onSuccess();
    } catch (err) {
      const errorMessage = "There was a problem sending your message. Please try again.";

      console.error(errorMessage);

      triggerEvent("sak_message_error", {
        error_message: errorMessage,
        error_type: "tour_post_checkout_error",
        topics
      });

      wvusLogger.log({ message: errorMessage, error: err }, wvusLogger.getLogLevelNames().ERROR);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    childData,
    childId,
    campaignSourceCode,
    eletterConfig,
    limitedTrustHandshake,
    limitedTrustMarker,
    supporterName,
    supporterNumber,
    supporterState,
    uploadedPhotoData,
    ELETTER_TIMEOUT_MS,
    setLoading,
    onSuccess,
    onError
  ]);

  return { sendEletter };
}
